import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useReviewReminder } from "@/hooks/use-review-reminder";
import { shouldShowReminder, dismissReminder } from "@/lib/review-reminder-storage";
import { ReviewReminderModal } from "./ReviewReminderModal";
import { usePageTourContext } from "@/contexts/PageTourContext";

/**
 * Wrapper component that handles the logic for showing the ReviewReminderModal.
 * - Checks if user is authenticated
 * - Checks if reminder was already dismissed today
 * - Only shows modal if there are items due for review
 * - Defers showing until any active tour or welcome modal is dismissed
 */
export const ReviewReminderProvider = () => {
    const { data: isAuthenticated, userId, isLoading: authLoading } = useAuth();
    const { vocabularyDue, vocabularyDueItems, videosDue, hasItemsDue, isLoading: dataLoading } = useReviewReminder();
    const { isTourActive } = usePageTourContext();
    const tourActiveRef = useRef(isTourActive);
    tourActiveRef.current = isTourActive;
    const [showModal, setShowModal] = useState(false);
    const [dismissalChecked, setDismissalChecked] = useState(false);
    const [wasDismissedToday, setWasDismissedToday] = useState(false);
    const [pendingShow, setPendingShow] = useState(false);

    // Check if we should show the modal once auth and data are loaded
    useEffect(() => {
        // Wait for auth to load
        if (authLoading) return;

        // Must be authenticated
        if (!isAuthenticated || !userId) {
            setShowModal(false);
            return;
        }

        // Wait for data to load
        if (dataLoading) return;

        // Check dismissal state (only once)
        if (!dismissalChecked) {
            setDismissalChecked(true);
            const dismissed = !shouldShowReminder(userId);
            setWasDismissedToday(dismissed);

            if (dismissed) {
                setShowModal(false);
                return;
            }
        }

        // Don't show if already dismissed today
        if (wasDismissedToday) {
            return;
        }

        // Only show if there are items due
        if (hasItemsDue) {
            // Defer if a tour or welcome modal is currently active
            if (isTourActive) {
                setPendingShow(true);
                setShowModal(false);
                return;
            }
            setShowModal(true);
        }
    }, [isAuthenticated, userId, authLoading, dataLoading, hasItemsDue, dismissalChecked, wasDismissedToday, isTourActive, showModal]);

    // When the tour/welcome modal closes, show the pending review reminder.
    // Use a short delay so that a rapid false→true transition (e.g. welcome
    // modal closes → tour immediately starts) doesn't flash the review modal.
    useEffect(() => {
        if (!isTourActive && pendingShow) {
            const timer = setTimeout(() => {
                // Re-check isTourActive after the delay — it may have flipped
                // back to true if a tour started immediately after the welcome modal.
                // We can't read isTourActive inside the timeout (stale closure),
                // so we use a ref to track the latest value.
                if (!tourActiveRef.current) {
                    setPendingShow(false);
                    setShowModal(true);
                }
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [isTourActive, pendingShow]);

    // Handle dismissal
    const handleDismiss = () => {
        if (userId) {
            dismissReminder(userId);
        }
        setWasDismissedToday(true);
        setShowModal(false);
    };

    // Handle modal close (same as dismiss)
    const handleOpenChange = (open: boolean) => {
        if (!open) {
            // If user closes modal by clicking outside or pressing escape,
            // we don't save the dismissal - they'll see it again on next page load
            setShowModal(false);
        }
    };

    // Don't render anything if not authenticated or loading
    if (!isAuthenticated || !userId || authLoading || dataLoading) {
        return null;
    }

    return (
        <ReviewReminderModal
            open={showModal}
            onOpenChange={handleOpenChange}
            vocabularyDue={vocabularyDue}
            vocabularyDueItems={vocabularyDueItems}
            videosDue={videosDue}
            onDismiss={handleDismiss}
        />
    );
};
